import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { safeChannel, supabase } from "./supabase";

// useTeamCookSession — the in-CookMode source of truth for participants
// and per-step assignments. Given a cook_sessions.id, this hook tails
// the two team tables (cook_session_participants + cook_step_assignments)
// over realtime and exposes the mutation calls every team surface needs.
//
// Pair with useCookTelemetry (single-user step writes) and
// useActiveCookSession (home banner). This hook is purely a team-data
// reader/writer.
//
// Contract:
//   {
//     participants,                 // array of cook_session_participants rows
//     participantsById,             // Map<user_id, row>
//     assignmentsByStep,            // Map<step_id, assignment[]>
//     myRole,                       // 'lead' | 'helper' | 'diner' | null
//     myColorTag,                   // string or null
//     canEdit,                      // role === 'lead' || 'helper'
//     assignStep({ stepId, stepTitle, assigneeIds, deadline }),
//     respondToAssignment({ assignmentId, accept }),  // accept=false → declined
//     markAssignmentDone(assignmentId),
//     unassignStep({ stepId, assigneeId }),
//     leaveSession(),
//     loading,
//   }
//
// Quiet on missing inputs: returns the empty-state shape and skips the
// subscription. Lets callers thread (userId, sessionId) through without
// guarding each render site.

const EMPTY_MAP = new Map();
const EMPTY_ARR = [];

export function useTeamCookSession({ userId, cookSessionId } = {}) {
  const [participants, setParticipants] = useState(EMPTY_ARR);
  const [assignments,  setAssignments]  = useState(EMPTY_ARR);
  const [loading, setLoading]           = useState(Boolean(cookSessionId));
  const aliveRef = useRef(true);

  // ── Initial fetch ──────────────────────────────────────────────────
  useEffect(() => {
    if (!cookSessionId) {
      setParticipants(EMPTY_ARR);
      setAssignments(EMPTY_ARR);
      setLoading(false);
      return undefined;
    }
    aliveRef.current = true;
    setLoading(true);
    (async () => {
      try {
        const [pRes, aRes] = await Promise.all([
          supabase
            .from("cook_session_participants")
            .select("*")
            .eq("cook_session_id", cookSessionId)
            .order("invited_at", { ascending: true }),
          supabase
            .from("cook_step_assignments")
            .select("*")
            .eq("cook_session_id", cookSessionId)
            .order("assigned_at", { ascending: true }),
        ]);
        if (!aliveRef.current) return;
        if (pRes.error) console.warn("[teamCook] participants fetch:", pRes.error.message);
        if (aRes.error) console.warn("[teamCook] assignments fetch:", aRes.error.message);
        setParticipants(pRes.data || EMPTY_ARR);
        setAssignments(aRes.data || EMPTY_ARR);
      } finally {
        if (aliveRef.current) setLoading(false);
      }
    })();
    return () => { aliveRef.current = false; };
  }, [cookSessionId]);

  // ── Realtime ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!cookSessionId) return undefined;

    const applyParticipant = (payload) => {
      setParticipants((prev) => {
        if (payload.eventType === "DELETE") {
          return prev.filter((r) => r.id !== payload.old?.id);
        }
        const next = payload.new;
        if (!next) return prev;
        const idx = prev.findIndex((r) => r.id === next.id);
        if (idx === -1) return [...prev, next];
        const copy = prev.slice();
        copy[idx] = next;
        return copy;
      });
    };

    const applyAssignment = (payload) => {
      setAssignments((prev) => {
        if (payload.eventType === "DELETE") {
          return prev.filter((r) => r.id !== payload.old?.id);
        }
        const next = payload.new;
        if (!next) return prev;
        const idx = prev.findIndex((r) => r.id === next.id);
        if (idx === -1) return [...prev, next];
        const copy = prev.slice();
        copy[idx] = next;
        return copy;
      });
    };

    const ch = safeChannel(`rt:team-cook:${cookSessionId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "cook_session_participants",
          filter: `cook_session_id=eq.${cookSessionId}`,
        },
        applyParticipant,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "cook_step_assignments",
          filter: `cook_session_id=eq.${cookSessionId}`,
        },
        applyAssignment,
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [cookSessionId]);

  // ── Derived maps ───────────────────────────────────────────────────
  const participantsById = useMemo(() => {
    const m = new Map();
    for (const p of participants) m.set(p.user_id, p);
    return m;
  }, [participants]);

  const assignmentsByStep = useMemo(() => {
    if (!assignments.length) return EMPTY_MAP;
    const m = new Map();
    for (const a of assignments) {
      const key = String(a.step_id);
      const list = m.get(key) || [];
      list.push(a);
      m.set(key, list);
    }
    return m;
  }, [assignments]);

  const myRow = userId ? participantsById.get(userId) : null;
  const myRole = myRow?.role || null;
  const myColorTag = myRow?.color_tag || null;
  const canEdit = myRole === "lead" || myRole === "helper";

  // ── Mutations ──────────────────────────────────────────────────────
  // assignStep({stepId, stepTitle, assigneeIds[], deadline}) →
  // one cook_step_assignments row per assignee. Fan-out trigger sends
  // the pending offers.
  const assignStep = useCallback(async ({ stepId, stepTitle, assigneeIds, deadline }) => {
    if (!cookSessionId || !userId) return null;
    if (!Array.isArray(assigneeIds) || !assigneeIds.length) return null;
    const rows = assigneeIds.map((uid) => ({
      cook_session_id: cookSessionId,
      step_id:         String(stepId),
      step_title:      stepTitle || null,
      assigned_to:     uid,
      assigned_by:     userId,
      deadline:        deadline ? new Date(deadline).toISOString() : null,
      status:          "pending",
    }));
    const { data, error } = await supabase
      .from("cook_step_assignments")
      .upsert(rows, { onConflict: "cook_session_id,step_id,assigned_to" })
      .select();
    if (error) {
      console.warn("[teamCook] assignStep failed:", error.message);
      return null;
    }
    return data;
  }, [cookSessionId, userId]);

  const respondToAssignment = useCallback(async ({ assignmentId, accept }) => {
    if (!assignmentId) return null;
    const patch = accept
      ? { status: "accepted", accepted_at: new Date().toISOString() }
      : { status: "declined", declined_at: new Date().toISOString() };
    const { data, error } = await supabase
      .from("cook_step_assignments")
      .update(patch)
      .eq("id", assignmentId)
      .select()
      .single();
    if (error) { console.warn("[teamCook] respond failed:", error.message); return null; }
    return data;
  }, []);

  const markAssignmentDone = useCallback(async (assignmentId) => {
    if (!assignmentId) return null;
    const { data, error } = await supabase
      .from("cook_step_assignments")
      .update({ status: "done", done_at: new Date().toISOString() })
      .eq("id", assignmentId)
      .select()
      .single();
    if (error) { console.warn("[teamCook] markDone failed:", error.message); return null; }
    return data;
  }, []);

  // Helper: when CookMode advances past a step via finishStep telemetry,
  // also auto-flip the current user's pending/accepted assignment for
  // that step to 'done'. The status-fan trigger closes sibling rows.
  const markStepDoneByCurrentUser = useCallback(async (stepId) => {
    if (!cookSessionId || !userId || stepId == null) return null;
    const stepKey = String(stepId);
    const mine = (assignments || []).find(
      (a) => String(a.step_id) === stepKey
        && a.assigned_to === userId
        && (a.status === "pending" || a.status === "accepted" || a.status === "in_progress"),
    );
    if (!mine) return null;
    return markAssignmentDone(mine.id);
  }, [assignments, cookSessionId, userId, markAssignmentDone]);

  const unassignStep = useCallback(async ({ stepId, assigneeId }) => {
    if (!cookSessionId || !stepId || !assigneeId) return null;
    const { error } = await supabase
      .from("cook_step_assignments")
      .delete()
      .eq("cook_session_id", cookSessionId)
      .eq("step_id", String(stepId))
      .eq("assigned_to", assigneeId);
    if (error) console.warn("[teamCook] unassign failed:", error.message);
    return !error;
  }, [cookSessionId]);

  // Mid-cook invite: solo cooks who started without scheduling helpers
  // can pull people in from inside CookMode. Inserts:
  //   • the current user as 'lead' if they don't yet have a participant row
  //   • each helper/diner from the picks
  // BEFORE-INSERT trigger picks color_tag for each row.
  const inviteParticipants = useCallback(async ({ helperIds = [], dinerIds = [] } = {}) => {
    if (!cookSessionId || !userId) return null;
    const rows = [];
    const seen = new Set();
    const push = (uid, role) => {
      if (!uid || seen.has(uid)) return;
      seen.add(uid);
      rows.push({
        cook_session_id: cookSessionId,
        user_id:         uid,
        role,
        color_tag:       "",
        invited_by:      userId,
        ...(uid === userId ? { accepted_at: new Date().toISOString() } : {}),
      });
    };
    // Self as lead, but only if not already a participant.
    if (!participantsById.get(userId)) push(userId, "lead");
    for (const uid of helperIds) push(uid, "helper");
    for (const uid of dinerIds)  push(uid, "diner");
    if (!rows.length) return null;
    const { data, error } = await supabase
      .from("cook_session_participants")
      .upsert(rows, { onConflict: "cook_session_id,user_id", ignoreDuplicates: true })
      .select();
    if (error) {
      console.warn("[teamCook] inviteParticipants failed:", error.message);
      return null;
    }
    return data;
  }, [cookSessionId, userId, participantsById]);

  const leaveSession = useCallback(async () => {
    if (!cookSessionId || !userId) return null;
    const { error } = await supabase
      .from("cook_session_participants")
      .update({ left_at: new Date().toISOString() })
      .eq("cook_session_id", cookSessionId)
      .eq("user_id", userId);
    if (error) console.warn("[teamCook] leave failed:", error.message);
    return !error;
  }, [cookSessionId, userId]);

  // ── Ownership claims (migration 0215) ────────────────────────────
  //
  // claimStep   — first-tap-wins. Race-proof via pg_advisory_xact_lock
  //               server-side. Returns { ok, already_owner? } or
  //               { ok: false, reason: "already_claimed_by_other", owner }.
  // releaseStep — drop my own claim back to 'pending' so another
  //               helper (or the lead via take_over_step) can pick up.
  // takeOverStep — lead-only force-reassign. Closes prior assignee's
  //               row as done_by_other and claims for the lead.

  const claimStep = useCallback(async (stepId) => {
    if (!cookSessionId || !userId || stepId == null) return { ok: false, reason: "missing-args" };
    const { data, error } = await supabase.rpc("claim_step", {
      p_session_id: cookSessionId,
      p_step_id:    String(stepId),
    });
    if (error) {
      console.warn("[teamCook] claim_step failed:", error.message);
      return { ok: false, reason: "rpc-error", error };
    }
    return data || { ok: false, reason: "no-result" };
  }, [cookSessionId, userId]);

  const releaseStep = useCallback(async (stepId) => {
    if (!cookSessionId || !userId || stepId == null) return { ok: false, reason: "missing-args" };
    const { data, error } = await supabase.rpc("release_step", {
      p_session_id: cookSessionId,
      p_step_id:    String(stepId),
    });
    if (error) {
      console.warn("[teamCook] release_step failed:", error.message);
      return { ok: false, reason: "rpc-error", error };
    }
    return data || { ok: false, reason: "no-result" };
  }, [cookSessionId, userId]);

  const takeOverStep = useCallback(async (stepId) => {
    if (!cookSessionId || !userId || stepId == null) return { ok: false, reason: "missing-args" };
    const { data, error } = await supabase.rpc("take_over_step", {
      p_session_id: cookSessionId,
      p_step_id:    String(stepId),
    });
    if (error) {
      console.warn("[teamCook] take_over_step failed:", error.message);
      return { ok: false, reason: "rpc-error", error };
    }
    return data || { ok: false, reason: "no-result" };
  }, [cookSessionId, userId]);

  return {
    participants,
    participantsById,
    assignments,
    assignmentsByStep,
    myRole,
    myColorTag,
    canEdit,
    assignStep,
    respondToAssignment,
    markAssignmentDone,
    markStepDoneByCurrentUser,
    unassignStep,
    inviteParticipants,
    leaveSession,
    claimStep,
    releaseStep,
    takeOverStep,
    loading,
  };
}
